// The gauge: a 12 px meter with optional tick labels, the web app's side of mockups/kit/surface.css
// section 19 (.wp-gauge, .wp-gauge-wrap, .wp-gauge-ticks). Used for the production plan's time budget.
import { cn } from "@waypoint/surface";
import type { ReactNode } from "react";

export interface GaugeProps {
  /** The accessible name of the meter. */
  readonly label: string;
  readonly value: number;
  readonly max: number;
  /** The reading in words, for a screen reader: "12:00 used of 50:00, 38:00 remaining". */
  readonly valueText: string;
  /** Past the end: the bar is drawn in the stop colour. */
  readonly over?: boolean;
  /** A lighter fill, for a draft the owner has not saved. */
  readonly draft?: boolean;
  /** Tick labels under the bar (hidden from assistive technology and on narrow screens). */
  readonly ticks?: readonly ReactNode[];
}

export function Gauge({
  label,
  value,
  max,
  valueText,
  over = false,
  draft = false,
  ticks,
}: GaugeProps) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div className="grid gap-0.75">
      <div
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={valueText}
        data-over={over}
        className="relative h-3 overflow-hidden rounded-pill bg-wp-line"
      >
        <i
          style={{ width: `${String(pct)}%` }}
          className={cn(
            "absolute inset-y-0 left-0 rounded-pill transition-[width] duration-med ease-wp",
            over ? "bg-stop" : draft ? "bg-wp-accent/38" : "bg-wp-accent",
          )}
        />
      </div>
      {ticks === undefined ? null : (
        <div
          aria-hidden
          className="hidden justify-between text-11 text-wp-text-3 tabular-nums sm:flex"
        >
          {ticks.map((tick, i) => (
            <span key={i}>{tick}</span>
          ))}
        </div>
      )}
    </div>
  );
}
